import { Clock } from "lucide-react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";

import EmptyState from "@/components/ui/v2/empty-state";
import { HistoryEntryIcon } from "@/components/ui/v2/history";
import { RelativeTime } from "@/components/ui/v2/relative-time";
import { getSegmentChangeVariant } from "@/features/segments/history/changeVariant";
import { SEGMENT_TOUR_TARGET } from "@/features/segments/tour/constants";
import {
  isSegmentChangeType,
  SEGMENT_CHANGE_TYPE,
  type SegmentActivity,
  type SegmentChangeType,
} from "@/features/segments/types";
import { useTourTarget } from "@/features/tours/useTourTarget";

type RecentActivitySectionProps = {
  activities: SegmentActivity[];
};

const DESCRIPTION_KEYS: Partial<Record<SegmentChangeType, string>> = {
  [SEGMENT_CHANGE_TYPE.SEGMENT_CREATED]: "admin.segments.activity.created",
  [SEGMENT_CHANGE_TYPE.RULES_CHANGED]: "admin.segments.activity.rulesChanged",
  [SEGMENT_CHANGE_TYPE.TYPE_CHANGED]: "admin.segments.activity.typeChanged",
  [SEGMENT_CHANGE_TYPE.INVERSION_CHANGED]: "admin.segments.activity.inversionChanged",
  [SEGMENT_CHANGE_TYPE.VACANCIES_ADDED]: "admin.segments.activity.vacanciesAdded",
  [SEGMENT_CHANGE_TYPE.VACANCIES_REMOVED]: "admin.segments.activity.vacanciesRemoved",
  [SEGMENT_CHANGE_TYPE.OVERRIDES_CHANGED]: "admin.segments.activity.overridesChanged",
  [SEGMENT_CHANGE_TYPE.SEGMENT_ARCHIVED]: "admin.segments.activity.archived",
  [SEGMENT_CHANGE_TYPE.SEGMENT_ADDED_TO_CAMPAIGN]: "admin.segments.activity.addedToCampaign",
  [SEGMENT_CHANGE_TYPE.SEGMENT_REMOVED_FROM_CAMPAIGN]: "admin.segments.activity.removedFromCampaign",
  [SEGMENT_CHANGE_TYPE.CAMPAIGN_CREATED]: "admin.segments.activity.addedToCampaign",
  [SEGMENT_CHANGE_TYPE.CAMPAIGN_INTEGRATION_CREATED]: "admin.segments.activity.addedToModule",
};

const GENERIC_KEYS: Partial<Record<SegmentChangeType, string>> = {
  [SEGMENT_CHANGE_TYPE.SEGMENT_ADDED_TO_CAMPAIGN]: "admin.segments.activity.addedToCampaignGeneric",
  [SEGMENT_CHANGE_TYPE.SEGMENT_REMOVED_FROM_CAMPAIGN]: "admin.segments.activity.removedFromCampaignGeneric",
  [SEGMENT_CHANGE_TYPE.CAMPAIGN_CREATED]: "admin.segments.activity.addedToCampaignGeneric",
  [SEGMENT_CHANGE_TYPE.CAMPAIGN_INTEGRATION_CREATED]: "admin.segments.activity.addedToModuleGeneric",
};

const getDescriptionKey = (changeType: string, entityName: string | null) => {
  if (!isSegmentChangeType(changeType)) return undefined;
  if (entityName) return DESCRIPTION_KEYS[changeType];
  return GENERIC_KEYS[changeType] ?? DESCRIPTION_KEYS[changeType];
};

export function RecentActivitySection({
  activities,
}: RecentActivitySectionProps) {
  const { t } = useTranslation();
  const isEmpty = activities.length === 0;
  const targetRef = useTourTarget<HTMLElement>(SEGMENT_TOUR_TARGET.OVERVIEW_ACTIVITY);

  if (isEmpty) {
    return (
      <section ref={targetRef}>
        <h3 className="mb-3 text-sm font-semibold">
          {t("admin.segments.recentActivity", "Recent activity")}
        </h3>
        <EmptyState
          layout="inline"
          icon={Clock}
          title={t("admin.segments.noActivity", "No activity yet")}
          description={t(
            "admin.segments.noActivityDescription",
            "Changes to this segment will appear here.",
          )}
        />
      </section>
    );
  }

  return (
    <section>
      <h3 className="mb-3 text-sm font-semibold">
        {t("admin.segments.recentActivity", "Recent activity")}
      </h3>
      <div className="flex flex-col gap-2">
        {activities.map((activity, idx) => {
          const { changeType, entityName } = activity;
          const key = getDescriptionKey(changeType, entityName);
          const description = key
            ? entityName
              ? (t(key, { name: entityName }) as string)
              : (t(key) as string)
            : changeType;

          return (
            <motion.div
              key={`${activity.occurredAt}-${activity.changeType}-${activity.entityName}`}
              ref={idx === 0 ? targetRef : undefined}
              initial={{ opacity: 0, x: -8 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.2, delay: idx * 0.05 }}
              data-slot="panel"
              className="flex items-center gap-3 rounded-lg border border-border bg-card p-3"
            >
              <HistoryEntryIcon
                variant={getSegmentChangeVariant(activity.changeType)}
              />
              <div className="min-w-0 flex-1">
                <p className="text-sm">{description}</p>
                {activity.occurredAt && (
                  <RelativeTime
                    date={activity.occurredAt}
                    className="text-xs text-muted-foreground"
                  />
                )}
              </div>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}
